import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import {
  DataTable, Filters, FormModal, Notice, PageHeader, Search, Sel,
  arr, dOnly, errMsg, money, useAction, useTerms, type FieldDef,
} from '@/features/shared/ops/kit';
import { badge, opts, useClassOptions } from '../lib';

const label = (s: string) =>
  (s ?? '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function studentName(r: any): string {
  const from = [r?.student?.first_name, r?.student?.last_name].filter(Boolean).join(' ');
  const fallback = `Student #${r?.student_id ?? '—'}`;
  return r?.name ?? r?.student_name ?? from ?? fallback;
}

function admissionNo(r: any): string {
  return r?.admission_no ?? r?.student?.admission_no ?? '—';
}

/* ---------- hold modal ---------- */
function HoldModal({ resultId, onClose, onChanged }: {
  resultId: number; onClose: () => void; onChanged: () => void;
}) {
  const a = useAction(onChanged);
  const fields: FieldDef[] = [
    { name: 'reason', label: 'Reason for holding', type: 'textarea', required: true, maxLength: 255,
      help: 'Shown to the parent when they ask why results are blocked.' },
  ];
  return (
    <FormModal
      title="Hold result"
      fields={fields}
      submitLabel="Hold"
      onClose={onClose}
      onSubmit={async (v) => {
        const res = await call('post', `/school/results/${resultId}/hold`, { body: { reason: v.reason } });
        a.setMsg(res.message ?? 'Result held.');
        onChanged();
        onClose();
      }}
    />
  );
}

/* ---------- main page ---------- */
export function ResultApprovalPage() {
  const qc = useQueryClient();
  const { options: classOpts } = useClassOptions();
  const termsQ = useTerms();
  const termOpts = useMemo(
    () => opts(arr(termsQ.data), (t: any) => t.name),
    [termsQ.data]
  );

  const [classId, setClassId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [holdResultId, setHoldResultId] = useState<number | null>(null);
  const [q, setQ] = useState('');

  const ready = classId !== '' && termId !== '';

  const resultsQ = useQuery({
    queryKey: ['desk-results-class', classId, termId],
    queryFn: () => call('get', '/school/results/class', {
      params: { class_id: classId, term_id: termId },
    }).then((r: any) => r.data),
    enabled: ready,
  });

  const data: any = resultsQ.data;
  const results = arr(data?.results) as any[];
  const counts = data?.counts ?? {};

  const totals = useMemo(() => ({
    computed: counts.computed ?? results.filter((r) => r.status === 'computed').length,
    approved: counts.approved ?? results.filter((r) => r.status === 'approved').length,
    published: counts.published ?? results.filter((r) => r.status === 'published').length,
    held: counts.held ?? results.filter((r) => r.status === 'held').length,
    total: results.length,
  }), [counts, results]);

  const [actionMsg, setActionMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [acting, setActing] = useState(false);

  const approve = async () => {
    setActing(true);
    setActionMsg(null);
    try {
      const res = await call('post', '/school/results/approve', {
        body: { class_id: Number(classId), term_id: Number(termId) },
      });
      setActionMsg({ kind: 'ok', text: res.message ?? `${res.data?.approved ?? 0} result(s) approved.` });
      qc.invalidateQueries({ queryKey: ['desk-results-class', classId, termId] });
    } catch (e: any) {
      setActionMsg({ kind: 'error', text: errMsg(e) });
    } finally { setActing(false); }
  };

  const publish = async () => {
    setActing(true);
    setActionMsg(null);
    try {
      const res = await call('post', '/school/results/publish', {
        body: { class_id: Number(classId), term_id: Number(termId) },
      });
      const d = res.data ?? {};
      setActionMsg({ kind: 'ok', text: res.message ?? `${d.published ?? 0} published, ${d.held ?? 0} held.` });
      qc.invalidateQueries({ queryKey: ['desk-results-class', classId, termId] });
    } catch (e: any) {
      setActionMsg({ kind: 'error', text: errMsg(e) });
    } finally { setActing(false); }
  };

  const release = async (resultId: number) => {
    setActionMsg(null);
    try {
      const res = await call('post', `/school/results/${resultId}/release`);
      setActionMsg({ kind: 'ok', text: res.message ?? 'Result released.' });
      qc.invalidateQueries({ queryKey: ['desk-results-class', classId, termId] });
    } catch (e: any) {
      setActionMsg({ kind: 'error', text: errMsg(e) });
    }
  };

  const filteredResults = q
    ? results.filter((r) => {
        const n = studentName(r).toLowerCase();
        const adm = admissionNo(r).toLowerCase();
        const lq = q.toLowerCase();
        return n.includes(lq) || adm.includes(lq);
      })
    : results;

  return (
    <div>
      <PageHeader
        title="Result approval"
        sub="Review computed results and approve them so they can be published to parents"
      />

      <div className="space-y-4">
        <Card title="Choose a class and term">
          <Filters>
            <Sel value={String(classId)} onChange={(v) => setClassId(v ? Number(v) : '')}
              placeholder="Choose class" options={classOpts} />
            <Sel value={String(termId)} onChange={(v) => setTermId(v ? Number(v) : '')}
              placeholder="Choose term" options={termOpts} />
          </Filters>
        </Card>

        {!ready && (
          <Card>
            <p className="text-sm text-slate-500 py-4 text-center">
              Pick a class and term above to load results.
            </p>
          </Card>
        )}

        {ready && (
          <>
            {actionMsg && (
              <Notice kind={actionMsg.kind === 'ok' ? 'ok' : 'error'}>{actionMsg.text}</Notice>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Computed', value: totals.computed, tone: 'text-blue-600' },
                { label: 'Approved', value: totals.approved, tone: 'text-violet-600' },
                { label: 'Published', value: totals.published, tone: 'text-green-600' },
                { label: 'Held', value: totals.held, tone: 'text-amber-600' },
              ].map((k) => (
                <div key={k.label} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-xs font-semibold text-slate-500">{k.label}</p>
                  <p className={`text-2xl font-extrabold font-mono ${k.tone}`}>{k.value}</p>
                </div>
              ))}
            </div>

            <Card title="Actions">
              <div className="flex flex-wrap gap-2">
                <Button onClick={approve} disabled={acting || totals.computed === 0}>
                  {acting ? 'Working…' : `Approve ${totals.computed} computed`}
                </Button>
                <Button onClick={publish} disabled={acting || totals.approved === 0} variant="ghost">
                  Publish {totals.approved} approved
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Approve moves <b>computed</b> → <b>approved</b>. Publish moves <b>approved</b> →{' '}
                <b>published</b> (or <b>held</b> if the student has unpaid fees and fee-gating is on).
              </p>
            </Card>

            {resultsQ.isLoading && <Card><p className="text-sm text-slate-500 py-4 text-center">Loading results…</p></Card>}
            {resultsQ.error && <Notice kind="error">{errMsg(resultsQ.error)}</Notice>}

            {!resultsQ.isLoading && !resultsQ.error && (
              <Card title={`Results (${filteredResults.length})`}>
                <Filters>
                  <Search value={q} onChange={setQ} placeholder="Search name or admission #" />
                </Filters>
                <DataTable
                  rows={filteredResults}
                  empty={data?.students_in_class > 0
                    ? `No results computed yet. ${data.without_result?.length ?? 0} student(s) without a result.`
                    : 'No results for this class yet.'}
                  cols={[
                    {
                      head: 'Student',
                      cell: (r: any) => (
                        <span>
                          <span className="block">{studentName(r)}</span>
                          <span className="block text-xs text-slate-500">{admissionNo(r)}</span>
                        </span>
                      ),
                    },
                    { head: 'Total', right: true, cell: (r: any) => r.total != null ? money(r.total) : '—' },
                    { head: 'Average', right: true, cell: (r: any) => r.average != null ? Number(r.average).toFixed(1) : '—' },
                    { head: 'Position', right: true, cell: (r: any) => r.position ?? '—' },
                    { head: 'Grade', cell: (r: any) => r.grade ?? '—' },
                    { head: 'Status', cell: (r: any) => badge(r.status ?? '—') },
                    {
                      head: '',
                      right: true,
                      cell: (r: any) => {
                        if (r.status === 'held') {
                          return <button onClick={() => release(r.result_id)} className="text-xs font-semibold text-green-700 hover:underline">Release</button>;
                        }
                        if (r.status === 'approved' || r.status === 'computed') {
                          return <button onClick={() => setHoldResultId(r.result_id)} className="text-xs font-semibold text-amber-700 hover:underline">Hold</button>;
                        }
                        return null;
                      },
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </div>

      {holdResultId !== null && (
        <HoldModal
          resultId={holdResultId}
          onClose={() => setHoldResultId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['desk-results-class', classId, termId] })}
        />
      )}
    </div>
  );
}
